import { useMemo } from 'react';

/* ---------- types ------------------------------------------------- */

type UnknownProps =
  | Record<
      string,
      | null // API sometimes really returns `null`
      | {
          value: any;
          name: string;
          unit: string | null;
          category?: {
            id: number;
            name: string;
            subCategory?: { id: number; name: string };
          };
        }
    >
  | undefined;

export interface CustomProp {
  key: string;
  name: string; // unique key
  value: any;
  unit?: string | null;
  category?: {
    id: number;
    name?: string;
    subCategory?: { id: number; name?: string };
  };
}

interface UseEnhancedPropertiesOptions {
  categoryOrder?: number[];
  blacklistCategories?: number[];
  blacklistSubCategories?: number[];
  blacklistKeys?: string[];
  yesLabel?: string;
  noLabel?: string;
  extraProperties?: CustomProp[];
}

/* ---------- core -------------------------------------------------- */

const useEnhancedPropertiesRaw = (
  properties: UnknownProps,
  {
    categoryOrder = [],
    blacklistCategories = [],
    blacklistSubCategories = [],
    blacklistKeys = [],
    yesLabel = 'Yes',
    noLabel = 'No',
    extraProperties = [],
  }: UseEnhancedPropertiesOptions = {},
) =>
  useMemo(() => {
    /* tuple helper */
    type CombinedTuple = [string, Required<CustomProp>];

    const combined: CombinedTuple[] = [];

    /* API props ---------------------------------------------------- */
    for (const [key, p] of Object.entries(properties || {})) {
      if (!p) continue; // skip null/undefined items
      combined.push([
        key,
        {
          key,
          name: p.name,
          value: p.value,
          unit: p.unit ?? null,
          category: p.category
            ? {
                id: p.category.id,
                name: p.category.name ?? '_api',
                subCategory: p.category.subCategory,
              }
            : {
                id: -1,
                name: '_uncategorised',
                subCategory: undefined,
              },
        },
      ]);
    }

    /* custom props ------------------------------------------------- */
    for (const p of extraProperties) {
      combined.push([
        p.key,
        {
          key: p.key,
          name: p.name,
          value: p.value,
          unit: p.unit ?? null,
          category: p.category
            ? {
                id: p.category.id,
                name: p.category.name ?? '_custom',
                subCategory: p.category.subCategory && {
                  id: p.category.subCategory.id,
                  name: p.category.subCategory.name ?? '_custom',
                },
              }
            : {
                id: -1,
                name: '_uncategorised',
                subCategory: undefined,
              },
        },
      ]);
    }

    /* grouping ----------------------------------------------------- */
    type CatMap = Map<
      number,
      {
        id: number;
        name: string;
        subCategories: Map<
          number,
          {
            id: number;
            name: string;
            items: Map<
              string,
              {
                key: string;
                name: string;
                unit: string | null;
                rawValue: any;
                displayValue: string | number;
              }
            >;
          }
        >;
      }
    >;

    const cats: CatMap = new Map();

    combined.forEach(([key, prop]) => {
      if (blacklistKeys.includes(key)) return;
      if (prop.value == null) return;
      if (blacklistCategories.includes(prop.category.id)) return;

      const subId = prop.category.subCategory?.id ?? -1;
      if (blacklistSubCategories.includes(subId)) return;

      const displayValue =
        typeof prop.value === 'boolean'
          ? prop.value
            ? yesLabel
            : noLabel
          : prop.unit
            ? `${prop.value} ${prop.unit}`
            : prop.value;

      /* top category */
      if (!cats.has(prop.category.id)) {
        cats.set(prop.category.id, {
          id: prop.category.id,
          name: prop.category.name ?? '_unknown',
          subCategories: new Map(),
        });
      }
      const cat = cats.get(prop.category.id)!;

      /* sub-category */
      if (!cat.subCategories.has(subId)) {
        cat.subCategories.set(subId, {
          id: subId,
          name: prop.category.subCategory?.name ?? '_uncategorised',
          items: new Map(),
        });
      }
      const subCat = cat.subCategories.get(subId)!;

      /* de-dupe on key */
      if (!subCat.items.has(key)) {
        subCat.items.set(key, {
          key,
          name: prop.name,
          unit: prop.unit,
          rawValue: prop.value,
          displayValue,
        });
      }
    });

    /* maps → arrays & ordering ------------------------------------ */
    const result = Array.from(cats.values()).map((c) => ({
      ...c,
      subCategories: Array.from(c.subCategories.values()).map((sc) => ({
        ...sc,
        items: Array.from(sc.items.values()),
      })),
    }));

    if (categoryOrder.length) {
      result.sort((a, b) => {
        const ia = categoryOrder.indexOf(a.id);
        const ib = categoryOrder.indexOf(b.id);
        return (
          (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib)
        );
      });
    }

    return result;
  }, [
    properties,
    extraProperties,
    categoryOrder.join(','),
    blacklistCategories.join(','),
    blacklistSubCategories.join(','),
    blacklistKeys.join(','),
    yesLabel,
    noLabel,
  ]);

/* ---------- defaults & wrapper ------------------------------------ */

const defaultCategoryOrder: number[] = [1337, 6, 8, 2, 1, 106];
const hiddenCats: number[] = [1000];
const hiddenSubs: number[] = [];
const hiddenKeys: string[] = [
  'maxTrailerWeightUnbraked',
  'gps',
  'enrichmentProviderEquipPackages',
  'enrichmentProviderExteriorPackages',
  'enrichmentProviderTrimPackages',
  'enrichmentProviderExteriorOptions',
  'towHitch',
];

export const defaultEnhancedPropertiesOptions: UseEnhancedPropertiesOptions = {
  categoryOrder: defaultCategoryOrder,
  blacklistCategories: hiddenCats,
  blacklistSubCategories: hiddenSubs,
  blacklistKeys: hiddenKeys,
  yesLabel: 'Ja',
  noLabel: 'Nej',
};

const useEnhancedProperties = (
  enhancedProperties: UnknownProps,
  options: UseEnhancedPropertiesOptions = defaultEnhancedPropertiesOptions,
) => useEnhancedPropertiesRaw(enhancedProperties, options);

export default useEnhancedProperties;
